import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../../environment";
import {
    createFileChargeRule,
    deleteFileChargeRule,
    getFileChargeRules,
    updateFileChargeRule,
} from "../../../apis/fileChargeRules";

const PenaltyRulesPage = () => {
    const tenantParams = () => {
        const user = JSON.parse(sessionStorage.getItem("info") || "{}");
        return { user_id: user.id, user_type: user.type };
    };
    const [rules, setRules] = useState<any[]>([]);
    const [fileChargeRules, setFileChargeRules] = useState<any[]>([]);
    const [fileChargeError, setFileChargeError] = useState("");
    const [fileChargeForm, setFileChargeForm] = useState({
        start_amount: "",
        end_amount: "",
        charge_type: "fixed",
        charge_amount: "",
        description: "",
    });
    const [form, setForm] = useState({
        name: "",
        enabled: true,
        penalty_type: "fixed",
        penalty_value: "",
        grace_days: "0",
        description: "",
    });

    const fetchRules = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/penalty-rules/`, { params: tenantParams() });
            setRules(res?.data?.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchFileChargeRules = async () => {
        try {
            const response = await getFileChargeRules();
            setFileChargeRules(response?.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchRules();
        fetchFileChargeRules();
    }, []);

    const submitFileChargeRule = async (e: React.FormEvent) => {
        e.preventDefault();
        setFileChargeError("");
        try {
            await createFileChargeRule({
                ...fileChargeForm,
                start_amount: Number(fileChargeForm.start_amount),
                end_amount: Number(fileChargeForm.end_amount),
                charge_amount: Number(fileChargeForm.charge_amount),
                enabled: true,
            });
            setFileChargeForm({ start_amount: "", end_amount: "", charge_type: "fixed", charge_amount: "", description: "" });
            fetchFileChargeRules();
        } catch (error: any) {
            setFileChargeError(error?.response?.data?.message || "Unable to save file charge slab");
        }
    };

    const toggleFileChargeRule = async (rule: any) => {
        setFileChargeError("");
        try {
            await updateFileChargeRule(rule.id, { enabled: !rule.enabled });
            fetchFileChargeRules();
        } catch (error: any) {
            setFileChargeError(error?.response?.data?.message || "Unable to update file charge slab");
        }
    };

    const removeFileChargeRule = async (id: number) => {
        setFileChargeError("");
        try {
            await deleteFileChargeRule(id);
            fetchFileChargeRules();
        } catch (error: any) {
            setFileChargeError(error?.response?.data?.message || "Unable to delete file charge slab");
        }
    };

    const togglePenaltyRule = async (rule: any) => {
        try {
            await axios.put(`${API_BASE_URL}/penalty-rules/${rule.id}`, { enabled: !rule.enabled, ...tenantParams() });
            fetchRules();
        } catch (error) {
            console.error(error);
        }
    };

    const submitRule = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await axios.post(`${API_BASE_URL}/penalty-rules/`, {
                ...form,
                ...tenantParams(),
                penalty_value: Number(form.penalty_value || 0),
                grace_days: Number(form.grace_days || 0),
            });
            setForm({
                name: "",
                enabled: true,
                penalty_type: "fixed",
                penalty_value: "",
                grace_days: "0",
                description: "",
            });
            fetchRules();
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <div className="page-wrapper">
            <div className="content">
            <div className="card">
                <div className="card-header bg-primary text-white">
                    <h4 className="mb-0">Penalty Rules</h4>
                </div>
                <div className="card-body">
                    <form onSubmit={submitRule} className="row g-3">
                        <div className="col-md-4">
                            <label className="form-label">Rule Name</label>
                            <input className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Enabled</label>
                            <select className="form-select" value={String(form.enabled)} onChange={(e) => setForm({ ...form, enabled: e.target.value === "true" })}>
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Penalty Type</label>
                            <select className="form-select" value={form.penalty_type} onChange={(e) => setForm({ ...form, penalty_type: e.target.value })}>
                                <option value="fixed">Fixed</option>
                                <option value="percentage">Percentage</option>
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Penalty Value</label>
                            <input className="form-control" type="number" value={form.penalty_value} onChange={(e) => setForm({ ...form, penalty_value: e.target.value })} required />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Grace Days</label>
                            <input className="form-control" type="number" value={form.grace_days} onChange={(e) => setForm({ ...form, grace_days: e.target.value })} />
                        </div>
                        <div className="col-md-9">
                            <label className="form-label">Description</label>
                            <input className="form-control" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        </div>
                        <div className="col-12">
                            <button className="btn btn-primary" type="submit">Save Rule</button>
                        </div>
                    </form>
                </div>
            </div>

            <div className="card">
                <div className="card-body">
                    <h5 className="mb-3">Existing Rules</h5>
                    <div className="table-responsive">
                        <table className="table table-bordered">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Type</th>
                                    <th>Value</th>
                                    <th>Grace Days</th>
                                    <th>Status</th>
                                    <th className="text-end">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rules.map((rule) => (
                                    <tr key={rule.id}>
                                        <td>{rule.name}</td>
                                        <td>{rule.penalty_type}</td>
                                        <td>{rule.penalty_value}</td>
                                        <td>{rule.grace_days}</td>
                                        <td>{rule.enabled ? "Enabled" : "Disabled"}</td>
                                        <td className="text-end">
                                            <button type="button" className={`btn btn-sm ${rule.enabled ? "btn-outline-warning" : "btn-outline-success"}`}
                                                onClick={() => togglePenaltyRule(rule)}>
                                                {rule.enabled ? "Deactivate" : "Activate"}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            </div>
        </div>
    );
};

export default PenaltyRulesPage;
